import { useEffect, useState } from "react";
import { TableCell, TableRow } from "@mui/material";
import OrderPreviewTableLayout, { OrderPreviewColumn, PreviewAccount, PreviewNumber } from "src/Components/OrderPreviewTableLayout";
import PreviewWarningsTable from "src/Tables/Order/PreviewWarningsTable";
import { IDeltaHedgeOrderFormValues } from "src/Interfaces/deltaHedgeOrder";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { IStepOptionDropdownItem, StepInstrumentType, StepOptionOrderType, StepOrderSide } from "src/Interfaces/stepInterfaces";
import { getUsers } from "src/Requests/connections";
import { getStepInstruments } from "src/Requests/stepRequests";
import { packetErrorHandler } from "src/Utils/requestUtils";

const processNames: Record<number, string> = { 1: "ورود", 2: "آفست", 3: "رول" };

export interface DeltaHedgePreviewLookups {
  accounts: DropdownItem[];
  instruments: IStepOptionDropdownItem[];
}

export const isDeltaHedgePreviewRowSendable = (value: IDeltaHedgeOrderFormValues | null | undefined) =>
  !!value && Number.isFinite(Number(value.baseVolume)) && Number(value.baseVolume) > 0;

const OrderPreviewTable = ({ values, lookups }: {
  values: IDeltaHedgeOrderFormValues[] | null;
  lookups?: DeltaHedgePreviewLookups;
}) => {
  const [fetchedAccounts, setAccounts] = useState<DropdownItem[]>([]);
  const [fetchedInstruments, setInstruments] = useState<IStepOptionDropdownItem[]>([]);
  useEffect(() => {
    if (lookups) return;
    getUsers().then(setAccounts).catch(packetErrorHandler);
    getStepInstruments().then(setInstruments).catch(packetErrorHandler);
  }, [lookups]);
  const accounts = lookups?.accounts ?? fetchedAccounts;
  const instruments = lookups?.instruments ?? fetchedInstruments;

  const rows = (values || []).filter(Boolean);
  const availableRows = rows.filter((value) => value.baseVolume != null && Number.isFinite(Number(value.baseVolume)));
  const accountName = (value: IDeltaHedgeOrderFormValues) =>
    value.accountName || value.userName || accounts.find((account) => String(account.id) === String(value.accountId))?.name || `اکانت ${value.accountId}`;
  const warningRows = availableRows.filter((value) => !isDeltaHedgePreviewRowSendable(value)).map((value) => ({
    userName: accountName(value),
    message: `${value.sheet ? `شیت ${value.sheet} · ردیف ${value.row ?? "—"}: ` : ""}حجم مرجع باید بیشتر از صفر باشد.`,
  }));
  const showInput = availableRows.some((value) => value.portfoPercent != null);
  const showFund = availableRows.some((value) => value.involvedFund != null);
  const columns: OrderPreviewColumn[] = [
    { key: "row", label: "#", width: 30 },
    { key: "account", label: "نام اکانت", width: 105 },
    { key: "process", label: "فرایند", width: 60 },
    { key: "leg1", label: "نماد اول", subtitle: "جهت / نوع معامله", width: 115 },
    { key: "leg2", label: "نماد دوم", subtitle: "جهت / نوع معامله", width: 115 },
    { key: "method", label: <>روش تعیین<br />حجم</>, width: 85 },
    ...(showInput ? [{ key: "input", label: <>مقدار<br />ورودی حجم</>, width: 75 }] : []),
    ...(showFund ? [{ key: "fund", label: <>سرمایه درگیر<br />مبنای محاسبه</>, width: 105 }] : []),
    { key: "side", label: <>طرف مرجع<br />حجم</>, width: 75 },
    { key: "volume", label: <>حجم مرجع<br />{showInput ? "محاسبه‌شده" : "سفارش"}</>, width: 90 },
    { key: "target", label: <>بازده قفل<br />هدف (%)</>, width: 75 },
    { key: "block", label: <>حجم هر بلوک<br />(%)</>, width: 65 },
    { key: "margin", label: <>مارجین<br />(%)</>, width: 60 },
  ];

  return <div className="flex w-full min-w-0 flex-col items-center gap-3" dir="rtl">
    <PreviewWarningsTable rows={warningRows} />
    <OrderPreviewTableLayout title="پیش‌نمایش دلتا هج" count={rows.length} columns={columns} minWidth={showInput || showFund ? 1000 : 900}>
    <>{rows.map((value, index) => {
      const key = `${value.accountId}-${value.sheet || "form"}-${value.row ?? index}`;
      if (value.baseVolume == null || !Number.isFinite(Number(value.baseVolume))) return <TableRow key={key}>
        <TableCell>{index + 1}</TableCell>
        <TableCell><PreviewAccount name="سفارش" sheet={value.sheet} row={value.row} canSend={false} /></TableCell>
        <TableCell colSpan={columns.length - 2}>{value.accessMessage || "اطلاعات این سفارش در دسترس نیست."}</TableCell>
      </TableRow>;
      return (
        <TableRow key={key}>
          <TableCell>{index + 1}</TableCell>
          <TableCell><PreviewAccount name={accountName(value)} sheet={value.sheet} row={value.row} canSend={isDeltaHedgePreviewRowSendable(value)} /></TableCell>
          <TableCell>{processNames[value.deltaHedgeOrderType] || "—"}</TableCell>
          {[1, 2].map((leg) => {
            const first = leg === 1;
            const id = first ? value.instrumentId1 : value.instrumentId2;
            const instrument = instruments.find((item) => String(item.id) === String(id));
            const side = first ? value.orderSide1 : value.orderSide2;
            const optionType = first ? value.optionOrderType1 : value.optionOrderType2;
            return <TableCell key={leg}>
              <div>{(first ? value.instrumentName1 : value.instrumentName2) || value.instrumentNames?.[leg - 1] || instrument?.name || id || "—"}</div>
              <div className="order-preview-secondary"><span>{side === StepOrderSide.Buy ? "خرید" : side === StepOrderSide.Sell ? "فروش" : "—"}</span>{optionType && (!instrument || instrument.type === StepInstrumentType.Option) ? <> · <span>{optionType === StepOptionOrderType.Offset ? "Offset" : "Open"}</span></> : null}</div>
            </TableCell>;
          })}
          <TableCell>{value.portfoPercent != null ? "درصد پورتفو" : "مقدار ثابت"}</TableCell>
          {showInput ? <TableCell><PreviewNumber value={value.portfoPercent != null ? value.originalVolumeInput ?? value.portfoPercent : value.baseVolume} suffix={value.portfoPercent != null ? " %" : ""} /></TableCell> : null}
          {showFund ? <TableCell><PreviewNumber value={value.involvedFund} /></TableCell> : null}
          <TableCell>{Number(value.baseVolumeSide) === 1 ? "نماد اول" : Number(value.baseVolumeSide) === 2 ? "نماد دوم" : "—"}</TableCell>
          <TableCell><PreviewNumber value={value.baseVolume} /></TableCell>
          <TableCell><PreviewNumber value={value.lockProfitTarget} /></TableCell>
          <TableCell><PreviewNumber value={value.blockSize} /></TableCell>
          <TableCell><PreviewNumber value={value.marginInPercent} /></TableCell>
        </TableRow>
      );
    })}</>
    </OrderPreviewTableLayout>
  </div>;
};

export default OrderPreviewTable;
